import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Anchor, Box, Container, Group, Stack, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useRequiredData } from '@/hooks/useRequiredData';
import { ROUTES } from '@/routes/paths';
import type { RequiredField } from '@/api/services/OrderService';

// Каждое недостающее поле → строка-варнинг со ссылкой на конкретный таб «Моей компании», а где
// возможно — с диплинком прямо к действию (?createNew — добавить счёт, ?addContact — добавить
// контакт). Реквизиты/контакты живут на разных табах, аттестация — на своём.
const FIELD_TO_ROW: Record<RequiredField, { readonly text: string; readonly to: string; readonly cta: string }> = {
  TAX_SYSTEM: { text: 'app.banner.taxSystem', to: `${ROUTES.APP_COMPANY}#tax`, cta: 'app.banner.ctaTax' },
  CONTACT_PHONE: { text: 'app.banner.contactPhone', to: `${ROUTES.APP_COMPANY}?addContact=true`, cta: 'app.banner.ctaContact' },
  BANK_REQUISITS: { text: 'app.banner.bank', to: `${ROUTES.APP_COMPANY_BANKING}?createNew=true`, cta: 'app.banner.ctaBank' },
  CERTIFICATION: { text: 'app.banner.certification', to: ROUTES.APP_COMPANY_ACCREDITATION, cta: 'app.banner.ctaCertification' },
  RESEND_CERTIFICATION: { text: 'app.banner.resendCertification', to: ROUTES.APP_COMPANY_ACCREDITATION, cta: 'app.banner.ctaResendCertification' },
  ADDRESS: { text: 'app.banner.address', to: `${ROUTES.APP_COMPANY}#addresses`, cta: 'app.banner.ctaAddress' },
  MANAGEMENT_NAME: { text: 'app.banner.managementName', to: `${ROUTES.APP_COMPANY}#management`, cta: 'app.banner.ctaManagement' },
  MANAGEMENT_POST: { text: 'app.banner.managementPost', to: `${ROUTES.APP_COMPANY}#management`, cta: 'app.banner.ctaManagementPost' },
  CONTRAGENT_BANK_REQUISITS: { text: 'app.banner.bank', to: `${ROUTES.APP_COMPANY_BANKING}?createNew=true`, cta: 'app.banner.ctaBank' },
  CONTRAGENT_CONTACT_PHONE: { text: 'app.banner.contactPhone', to: `${ROUTES.APP_COMPANY}?addContact=true`, cta: 'app.banner.ctaContact' },
  CONTRAGENT_ADDRESS: { text: 'app.banner.address', to: `${ROUTES.APP_COMPANY}#addresses`, cta: 'app.banner.ctaAddress' },
};

// Аттестация первой: без неё закрыта торговля, остальные данные идут за ней.
const FIRST: readonly RequiredField[] = ['CERTIFICATION', 'RESEND_CERTIFICATION'];

interface BannerRow {
  readonly text: string;
  readonly to?: string;
  readonly cta?: string;
}

// Баннер обязательных данных под хедером. Скрыт, если требований нет или запрос не удался.
export default function RequiredDataBanner() {
  const { t } = useTranslation();
  const { data } = useRequiredData();

  if (!data) {
    return null;
  }

  const rows: BannerRow[] = [];
  const seenFields = new Set<string>();
  const fields = [...data.requiredUserDataFields].sort(
    (a, b) => Number(FIRST.includes(b)) - Number(FIRST.includes(a)),
  );
  for (const field of fields) {
    const row = FIELD_TO_ROW[field];
    if (row && !seenFields.has(row.text)) {
      seenFields.add(row.text);
      rows.push({ text: t(row.text), to: row.to, cta: t(row.cta) });
    }
  }
  if (data.requiredBargainsToPay.length > 0) {
    rows.push({
      text: t('app.banner.unpaidDeals', { ids: data.requiredBargainsToPay.join(', ') }),
      to: ROUTES.APP_DEALS,
      cta: t('app.banner.toDeals'),
    });
  }

  // otherMessages: спец-константы обрабатываем явно (как старый портал), остальные строки —
  // как готовый текст. Сырые ALL_CAPS-константы в UI НЕ показываем (защита от утечки enum'ов).
  if (data.otherMessages.includes('RESEND_AGENCY_AGREEMENT')) {
    rows.push({ text: t('app.banner.resendAgency'), to: ROUTES.APP_COMPANY_AGENCY, cta: t('app.banner.goto') });
  }
  const CONSTANT_RE = /^[A-Z][A-Z0-9_]*$/;
  for (const message of new Set(data.otherMessages)) {
    const text = message.trim();
    if (!text || CONSTANT_RE.test(text)) {
      continue; // AGENCY_AGREEMENT / NOT_AGRICULTURAL_PRODUCER и прочие константы — не выводим сырыми
    }
    rows.push({ text });
  }

  if (rows.length === 0) {
    return null;
  }

  return (
    <Box bg="wheat.0" style={{ borderBottom: '1px solid var(--mantine-color-wheat-2)' }}>
      <Container size="lg" py="xs">
        <Stack gap={6} component="ul" m={0} p={0} style={{ listStyle: 'none' }}>
          {rows.map((row, index) => (
            <Group key={`${row.text}-${index}`} component="li" gap="sm" wrap="nowrap" align="flex-start">
              <IconAlertTriangle
                size={18}
                color="var(--mantine-color-wheat-7)"
                style={{ flexShrink: 0, marginTop: 2 }}
              />
              <Text size="sm" style={{ flex: 1 }}>
                {row.text}
              </Text>
              {row.to ? (
                <Anchor component={Link} to={row.to} size="sm" fw={500} c="brand.7" underline="always" style={{ flexShrink: 0 }}>
                  {row.cta}
                </Anchor>
              ) : null}
            </Group>
          ))}
        </Stack>
      </Container>
    </Box>
  );
}
